import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-input',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiInput),
      multi: true,
    },
  ],
  template: `
    @if (label()) {
      <label [for]="inputId()" class="block text-sm font-medium text-gray-700 mb-1">{{ label() }}</label>
    }
    <input
      [id]="inputId()"
      [type]="type()"
      [placeholder]="placeholder()"
      [disabled]="disabled()"
      [readonly]="readonly()"
      [class]="inputClasses()"
      [value]="value()"
      (input)="onInput($event)"
      (blur)="onTouched()"
      [attr.aria-label]="ariaLabel() || label() || placeholder()"
      [attr.aria-invalid]="invalid()"
      [attr.aria-disabled]="disabled()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiInput {
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly type = input<string>('text');
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly inputId = input<string>('input-' + Math.random().toString(36).substring(2, 9));
  readonly ariaLabel = input<string>('');
  readonly styleClass = input<string>('');

  readonly value = model<string>('');

  onTouched: () => void = () => {};
  private onChange: (value: string) => void = () => {};

  protected readonly inputClasses = computed(() => {
    const base = 'w-full px-3 py-2 text-sm border rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 placeholder:text-slate-400';
    const normal = 'border-slate-200 hover:border-slate-300 bg-white';
    const invalidCls = this.invalid() ? 'border-red-400 focus:ring-red-500/20 focus:border-red-500' : '';
    const disabled = this.disabled() ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : '';
    return `${base} ${this.invalid() ? invalidCls : normal} ${disabled} ${this.styleClass()}`.trim();
  });

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value.set(target.value);
    this.onChange(target.value);
  }

  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // handled via input
  }
}
